import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader } from "@/components/common";
import { MeetingRecorderButton } from "@/components/MeetingRecorder";
import { PerformanceCharts } from "@/components/PerformanceCharts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  GraduationCap, School, UsersRound, Percent, BookCheck, AlertTriangle,
  Award, FileSignature, UserPlus, ClipboardList, Loader2, TrendingUp, ScanFace,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
  AreaChart, Area, PieChart, Pie, Legend,
} from "recharts";

const STAT_DEFS = [
  { key: "students", label: "Alunos Matriculados", icon: GraduationCap, color: "text-blue-700", bg: "bg-blue-50", testid: "dashboard-stat-enrolled" },
  { key: "classes", label: "Turmas Ativas", icon: School, color: "text-indigo-700", bg: "bg-indigo-50" },
  { key: "teachers", label: "Professores", icon: UsersRound, color: "text-violet-700", bg: "bg-violet-50" },
  { key: "avg_attendance", label: "Frequência Média", icon: Percent, color: "text-emerald-700", bg: "bg-emerald-50", suffix: "%", testid: "dashboard-stat-attendance", phase: "Fase 2" },
  { key: "diaries_pending", label: "Diários Pendentes", icon: BookCheck, color: "text-amber-700", bg: "bg-amber-50", testid: "dashboard-stat-diaries", phase: "Fase 2" },
  { key: "occurrences_term", label: "Ocorrências no Trimestre", icon: AlertTriangle, color: "text-rose-700", bg: "bg-rose-50", testid: "dashboard-stat-occurrences", phase: "Fase 4" },
  { key: "excellence_students", label: "Excelência Acadêmica", icon: Award, color: "text-orange-700", bg: "bg-orange-50", phase: "Fase 5" },
  { key: "docs_awaiting", label: "Docs. Aguardando Assinatura", icon: FileSignature, color: "text-cyan-700", bg: "bg-cyan-50", phase: "Fase 4" },
];

const SHORTCUTS = [
  { label: "Cadastrar Aluno", icon: UserPlus, to: "/alunos", testid: "dashboard-quick-action-new-student", roles: ["SUPERADMIN", "DIRECAO", "COORDENACAO"] },
  { label: "Cadastrar Turma", icon: School, to: "/turmas", roles: ["SUPERADMIN", "DIRECAO", "COORDENACAO"] },
  { label: "Professores", icon: UsersRound, to: "/professores", roles: ["SUPERADMIN", "DIRECAO", "COORDENACAO", "PROFESSOR"] },
  { label: "Config. Acadêmica", icon: ClipboardList, to: "/configuracoes-academicas", roles: ["SUPERADMIN", "DIRECAO", "COORDENACAO"] },
];

const SHIFTS = ["Manhã", "Tarde", "Noite", "Integral"];

function ShiftBreakdown({ by }) {
  return (
    <div className="mt-3 grid grid-cols-2 gap-1.5 border-t border-slate-100 pt-2.5" data-testid="dashboard-students-by-shift">
      {SHIFTS.map((s) => (
        <div key={s} className="flex items-center justify-between rounded-md bg-blue-50/60 px-2 py-1 text-[11px]">
          <span className="font-medium text-slate-600">{s}</span>
          <span className="font-bold text-blue-800" data-testid={`dashboard-shift-${s.toLowerCase()}`}>{by[s] ?? 0}</span>
        </div>
      ))}
      {by["Sem turno"] > 0 && <div className="col-span-2 text-[10px] text-slate-400" data-testid="dashboard-shift-none">Sem turno definido: {by["Sem turno"]}</div>}
    </div>
  );
}


export default function Dashboard() {
  const { user, scopeParams, selectedSchoolId, isSuperadmin } = useAuth();
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    setData(null);
    api.get("/dashboard", { params: scopeParams }).then((r) => setData(r.data)).catch(() => setData(false));
  }, [scopeParams]);

  if (data === null)
    return <div className="grid h-64 place-items-center"><Loader2 className="h-7 w-7 animate-spin text-blue-700" /></div>;

  const m = data?.metrics || {};
  const fmt = (v, s) => (v === null || v === undefined ? "—" : `${v}${s || ""}`);
  const chartData = (data?.students_by_class || []).map((c) => ({ name: c.name, alunos: c.count }));
  const colors = ["#1E40AF", "#EA580C", "#0891B2", "#7C3AED", "#16A34A"];

  return (
    <div>
      <PageHeader title="Dashboard Institucional"
        subtitle={isSuperadmin && !selectedSchoolId ? "Visão consolidada de todas as escolas" : "Indicadores da unidade escolar"} />

      {["SUPERADMIN", "DIRECAO", "COORDENACAO"].includes(user.role) && !(isSuperadmin && !selectedSchoolId) && (
        <Card className="mb-5 flex flex-col gap-3 border-red-100 bg-gradient-to-r from-red-50 to-white p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="dashboard-meeting-card">
          <div>
            <h2 className="font-display text-base font-bold text-slate-900 md:text-lg">Reuniões e atendimentos</h2>
            <p className="text-sm text-slate-500">Grave a conversa; a IA transcreve e redige a Ata, o Relatório ou o Plano de Ação.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => navigate("/reunioes")} data-testid="dashboard-meetings-history">Histórico</Button>
            <MeetingRecorderButton scopeParams={scopeParams} />
          </div>
        </Card>
      )}

      {/* Metrics */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {STAT_DEFS.map((s) => (
          <Card key={s.key} data-testid={s.testid} className="card-hover border-slate-200 p-4">
            <div className="flex items-start justify-between">
              <div className={`grid h-10 w-10 place-items-center rounded-xl ${s.bg}`}>
                <s.icon className={`h-5 w-5 ${s.color}`} />
              </div>
              {s.phase && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-400">{s.phase}</span>}
            </div>
            <div className="mt-3 font-display text-2xl font-black tracking-tight text-slate-900 sm:text-3xl">
              {fmt(m[s.key], s.suffix)}
            </div>
            <div className="mt-0.5 text-xs font-medium text-slate-500">{s.label}</div>
            {s.key === "students" && data?.students_by_shift && <ShiftBreakdown by={data.students_by_shift} />}
          </Card>
        ))}
      </div>

      {/* Shortcuts */}
      <div className="mt-6">
        <h2 className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-400">Atalhos</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {SHORTCUTS.filter((s) => s.roles.includes(user.role)).map((s) => (
            <button key={s.label} data-testid={s.testid} onClick={() => navigate(s.to)}
              className="card-hover flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left">
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-blue-700 text-white"><s.icon className="h-5 w-5" /></div>
              <span className="text-sm font-semibold text-slate-700">{s.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      <Card className="mt-6 border-slate-200 p-5">
        <div className="mb-4 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-blue-700" />
          <h3 className="font-display text-lg font-semibold text-slate-800">Alunos por Turma</h3>
        </div>
        {chartData.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-400">Sem turmas cadastradas.</p>
        ) : (
          <div className="h-72 w-full" style={{ minHeight: 288 }}>
            <ResponsiveContainer width="100%" height={288}>
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#64748b" }} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
                <Bar dataKey="alunos" radius={[8, 8, 0, 0]}>
                  {chartData.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <AnalyticsCharts scopeParams={scopeParams} selectedSchoolId={selectedSchoolId} />
      {["SUPERADMIN", "DIRECAO", "COORDENACAO"].includes(user.role) && !(isSuperadmin && !selectedSchoolId) && <PerformanceCharts scopeParams={scopeParams} schoolKey={selectedSchoolId} />}
      <LiveArrivals scopeParams={scopeParams} />
    </div>
  );
}

const CHART_ICONS = { trend: TrendingUp };

function AnalyticsCharts({ scopeParams, selectedSchoolId }) {
  const [a, setA] = useState(null);
  useEffect(() => {
    setA(null);
    api.get("/dashboard/analytics", { params: scopeParams }).then((r) => setA(r.data)).catch(() => setA(false));
    // eslint-disable-next-line
  }, [selectedSchoolId]);

  if (a === null)
    return <div className="mt-6 grid h-40 place-items-center rounded-2xl border border-slate-200 bg-white"><Loader2 className="h-6 w-6 animate-spin text-blue-700" /></div>;
  if (a === false) return null;

  const trend = (a.freq_trend || []).map((d) => ({
    ...d, label: d.date.slice(8, 10) + "/" + d.date.slice(5, 7),
  }));
  const exc = a.excellence_by_term || [];
  const awards = (a.awards_distribution || []).filter((x) => x.value > 0);
  const awardColors = { OURO: "#F59E0B", PRATA: "#94A3B8", BRONZE: "#B45309" };
  const hasAwards = awards.length > 0;
  const hasExc = exc.some((e) => e.classified + e.not_classified > 0);
  const hasTrend = trend.some((t) => t.chegadas + t.faltas + t.saidas > 0);

  return (
    <div className="mt-6 space-y-6">
      <Card className="border-slate-200 p-5" data-testid="dashboard-freq-trend">
        <div className="mb-4 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-emerald-600" />
          <h3 className="font-display text-lg font-semibold text-slate-800">Tendência de Frequência</h3>
          <span className="ml-auto text-xs text-slate-400">Últimos {trend.length} dias</span>
        </div>
        {!hasTrend ? (
          <p className="py-10 text-center text-sm text-slate-400">Sem registros de presença no período.</p>
        ) : (
          <div className="h-64 w-full" style={{ minHeight: 256 }}>
            <ResponsiveContainer width="100%" height={256}>
              <AreaChart data={trend} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gChegada" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gFalta" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#F43F5E" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#F43F5E" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#64748b" }} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="chegadas" name="Chegadas" stroke="#10B981" fill="url(#gChegada)" strokeWidth={2} />
                <Area type="monotone" dataKey="faltas" name="Faltas" stroke="#F43F5E" fill="url(#gFalta)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="border-slate-200 p-5" data-testid="dashboard-excellence-dist">
          <div className="mb-4 flex items-center gap-2">
            <Award className="h-5 w-5 text-blue-700" />
            <h3 className="font-display text-lg font-semibold text-slate-800">Excelência por Trimestre</h3>
          </div>
          {!hasExc ? (
            <p className="py-10 text-center text-sm text-slate-400">Sem resultados de excelência calculados.</p>
          ) : (
            <div className="h-64 w-full" style={{ minHeight: 256 }}>
              <ResponsiveContainer width="100%" height={256}>
                <BarChart data={exc} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="term" tick={{ fontSize: 12, fill: "#64748b" }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#64748b" }} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="classified" name="Classificados" stackId="a" fill="#1E40AF" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="not_classified" name="Não classificados" stackId="a" fill="#CBD5E1" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card className="border-slate-200 p-5" data-testid="dashboard-awards-dist">
          <div className="mb-4 flex items-center gap-2">
            <Award className="h-5 w-5 text-orange-600" />
            <h3 className="font-display text-lg font-semibold text-slate-800">Premiações do Ano</h3>
          </div>
          {!hasAwards ? (
            <p className="py-10 text-center text-sm text-slate-400">Sem premiações homologadas.</p>
          ) : (
            <div className="h-64 w-full" style={{ minHeight: 256 }}>
              <ResponsiveContainer width="100%" height={256}>
                <PieChart>
                  <Pie data={awards} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={88} innerRadius={48} paddingAngle={3} label={(e) => `${e.name}: ${e.value}`}>
                    {awards.map((e) => <Cell key={e.category} fill={awardColors[e.category]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function LiveArrivals({ scopeParams }) {
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    const load = () => {
      const today = new Date().toISOString().slice(0, 10);
      api.get("/attendance", { params: { ...scopeParams, date: today, type: "CHEGADA" } })
        .then((r) => setRows(r.data)).catch(() => {});
    };
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 30000);
    return () => clearInterval(t);
  }, [scopeParams]);
  return (
    <Card className="mt-6 border-slate-200 p-5" data-testid="dashboard-live-arrivals">
      <div className="mb-4 flex items-center gap-2">
        <ScanFace className="h-5 w-5 text-emerald-600" />
        <h3 className="font-display text-lg font-semibold text-slate-800">Chegadas de Hoje</h3>
        <span className="ml-auto rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-bold text-emerald-700" data-testid="live-arrivals-count">{rows.length}</span>
        <button onClick={() => navigate("/presenca")} className="text-xs font-medium text-blue-700 hover:underline">Ver presença</button>
      </div>
      {rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400">Nenhuma chegada registrada hoje.</p>
      ) : (
        <ul className="max-h-80 divide-y divide-slate-100 overflow-y-auto">
          {rows.slice(0, 60).map((r) => (
            <li key={r.id} className="flex items-center gap-3 py-2">
              <div className="grid h-8 w-12 place-items-center rounded-lg bg-emerald-100 text-xs font-bold text-emerald-700">{r.time}</div>
              <span className="flex-1 truncate text-sm font-medium text-slate-700">{r.student_name}</span>
              <span className="text-xs text-slate-400">{r.method === "FACIAL" ? "Facial" : r.method === "MATRICULA" ? "Matrícula" : "Manual"}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
